import { type JSX } from 'react';

import { type IconComponent } from '@/components/icons';
import { cn } from '@/lib/utils';

/**
 * Icon-only action button used in admin tables (campaigns, customers,
 * etc.). Renders a 28x28 hit area with a 16px icon and a native
 * tooltip on hover for accessibility (the label is also passed to
 * aria-label for screen readers).
 *
 * Three tones beyond the default neutral:
 *   - primary  → green tint, used for the row's main action (e.g. Launch)
 *   - warn     → amber tint, used for cancel/unschedule
 *   - danger   → red tint, used for delete
 */
export type IconActionTone = 'default' | 'primary' | 'warn' | 'danger';

export interface IconActionProps {
  icon: IconComponent;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: IconActionTone;
}

export function IconAction({
  icon: IconComp,
  label,
  onClick,
  disabled = false,
  tone = 'default',
}: IconActionProps): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        // Stop propagation so the surrounding row's onClick (when the
        // table row is clickable as a "go to detail" affordance) doesn't
        // also fire. Without this, clicking Edit would navigate to the
        // detail page AND open the edit form.
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      className={cn(
        'w-7 h-7 grid place-items-center rounded-md transition-colors',
        'text-text-3 hover:text-text hover:bg-surface-3',
        tone === 'primary' && 'text-volt-600 hover:bg-volt-glow hover:text-volt-700',
        tone === 'warn' && 'text-warn hover:bg-warn/10',
        tone === 'danger' && 'text-danger hover:bg-danger/10',
        disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent',
      )}
    >
      <IconComp width={16} height={16} />
    </button>
  );
}
